import type { MapPoint, MapPolygon } from '../map-style.ts';

/**
 * An outline while it is being drawn, as map content — shared by every place
 * an outline is drawn (the wizard's building and assembly areas, the building
 * page's boundary), so they all show it the same way.
 *
 * Every corner is a marker from the FIRST tap, the first two are joined by a
 * line, and from the third the shape fills (Trav, 2026-10-01: "the first 2
 * points don't show on the map, they only appear when I add a third"). The
 * corners are draggable when `draggable` is set, with ids `<id>-corner-<n>` —
 * {@link cornerIndex} reads the number back.
 */
export function drawingShape(
  ring: { latitude: number; longitude: number }[],
  id: string,
  kind: MapPolygon['kind'],
  draggable = false,
): { points: MapPoint[]; polygons: MapPolygon[] } {
  return {
    points: ring.map((c, i) => ({
      id: `${id}-corner-${i}`,
      latitude: c.latitude,
      longitude: c.longitude,
      label: '',
      kind: 'vertex' as const,
      draggable,
    })),
    polygons: ring.length >= 2 ? [{ id, ring, kind }] : [],
  };
}

/** The corner a marker id names, for a drawing with this id; null otherwise. */
export function cornerIndex(pointId: string, id: string): number | null {
  const prefix = `${id}-corner-`;
  if (!pointId.startsWith(prefix)) return null;
  const rest = pointId.slice(prefix.length);
  return /^\d+$/.test(rest) ? Number(rest) : null;
}
